// @flow strict-local

import type {
  QuestionNode,
  Answer,
  Id,
  ResponseType,
  EditableGraph,
  AnswerId,
} from 'src/types/chatbot';
import type {EntityType} from 'src/types/ats-entities';
import type {Validation} from 'src/utils/validation';
import type {CheckedValue} from 'src/designSystem2021Components/checkbox.jsx';

import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import without from 'lodash/without';

import classify from 'src/utils/classify';
import nanoid from 'src/utils/id';
import {
  useEditorState,
  stringErrorMessage,
  multipleChoiceErrorMessage,
  type QuestionDispatch,
} from './reducer';
import {useOWBFields} from '../hooks';
import {useWritebackMetadataV2} from 'src/hooks/useWritebackAllowedAttributes';
import {useReleaseFlag} from 'src/hooks/product-flags';

import {Checkbox} from 'src/designSystem2021Components/checkbox.jsx';

import {useSmsContext} from 'src/components/conversation-builder/sms-context';
import {
  getMultipleChoiceLabel,
  findAncestorNodeVariables,
  getDerivedAddressFields,
  getDataTypeOptions,
  writebackDataTypesToValidationTypes,
} from 'src/components/conversation-builder/utils';
import {validateNode} from './validation';

import {
  MouseTip,
  useHoverTip,
} from 'src/components/lib/mouse-tip/mouse-tip.jsx';

import HoverIconButton from 'src/components/lib/hover-icon-button';
import {Menu} from 'src/components/lib/token-list-input';
import Button from 'src/components/lib/new-button';
import OneLineTextInput from 'src/components/lib/text-input/one-line.jsx';
import MultiLineTextInput from 'src/components/lib/text-input/multi-line.jsx';
import EditorContainer from './container.jsx';
import {
  MessageDraftEditor,
  SMSLengthWarning,
} from 'src/components/conversation-builder/draft-editor.jsx';
import {
  FormPair,
  FormGroup,
} from 'src/components/conversation-builder/form.jsx';
import {
  PadSection,
  useFirstSmsMessage,
  Table,
  TableHeader,
  ColumnGrow,
  ColumnValue,
  ColumnEnabled,
  TableBody,
  TableRow,
  TableFooter,
} from './common.jsx';
import LoadedVariablePicker from './variable-picker.jsx';

import CancelIcon from 'src/images/icons/cancel-icon.svg';
import ComposeIcon from 'src/images/icons/compose-icon.svg';
import EditIcon from 'src/images/icons/edit-icon.svg';
import InfoIcon from 'src/images/icons/info.svg';
import ArrowDownIcon from 'src/images/arrow-down.svg';
import ArrowUpIcon from 'src/images/arrow-up.svg';

import css from 'src/components/conversation-builder/popover.css';


export default function QuestionEditor({
  graph,
  defaultNode,
  entityType,
  hasSubmitted,
  edgeId,
  ...props
}: {
  graph: EditableGraph,
  entityType: EntityType,
  onClose: () => mixed,
  onSubmit: (QuestionNode) => mixed,
  defaultNode: QuestionNode,
  actionName: 'add' | 'edit',
  hasSubmitted: boolean,
  readOnly: boolean,
  edgeId?: Id,
}): React.Node {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const {readOnly} = props;

  const isSms = useSmsContext();
  const isFirstSmsMessage = useFirstSmsMessage(graph, defaultNode);
  const [state, dispatch] = useEditorState({
    defaultNode,
  });
  const {node} = state;
  const update = (payload: $Shape<QuestionNode>) =>
    dispatch({type: 'update', payload});

  React.useEffect(() => {
    if (!node.question_error_message) {
      update({
        question_error_message: node.answers
          ? multipleChoiceErrorMessage
          : stringErrorMessage,
      });
    }
  }, []);

  // TODO (kyle): this causes some stuff to pop in because the writeback
  // info has to load async.
  const writebackAttributes = useWritebackMetadataV2(entityType);

  const {getWbFieldId} = useOWBFields();

  const {writeback_field_id} = node;
  const attributeWritebackInfo =
    writeback_field_id && writebackAttributes
      ? writebackAttributes.find(
          (metadata) => getWbFieldId(metadata) === writeback_field_id,
        )
      : null;

  const writebackDataType = attributeWritebackInfo?.data_type;

  const writebackResponseTypeOptions = [
    {
      value: 'buttons',
      label: isSms
        ? t('Singleselect', 'Single-select')
        : t('Singleselect_Buttons', 'Single-select Buttons'),
    },
    {
      value: 'multiselect',
      label: isSms
        ? t('Multiselect', 'Multi-select')
        : t('Multiselect_Buttons', 'Multi-select Buttons'),
    },
  ].filter((opt) => {
    if (attributeWritebackInfo != null) {
      if (
        !attributeWritebackInfo.allow_select_multiple &&
        opt.value === 'multiselect'
      ) {
        return false;
      }
    }
    return true;
  });

  const responseTypeOptions = [
    {value: null, label: t('Free_Form', 'Free Form')},
    ...writebackResponseTypeOptions,
  ];
  if (['string', 'boolean'].includes(node.response_type)) {
    responseTypeOptions.push({value: 'boolean', label: t('YesNo', 'Yes/No')});
  }

  const responseType =
    node.response_type === 'boolean'
      ? 'boolean'
      : node.answers
      ? node.question_is_multiselect
        ? 'multiselect'
        : 'buttons'
      : null;

  // NOTE (kyle): for certain writeback data types, the user can edit the
  // validation type. for others, they cannot.
  let hasFixedDataType;
  const dataTypeOptions = getDataTypeOptions(t);
  let validationTypeOptions = dataTypeOptions;

  // see CHAT-1694 for more context.
  // For now, we only allow address validation if we have this release flag enabled
  // but this doesn't prevent the feature from working if previously activated.
  // deactivating merely prevents creating new address validation nodes.
  if (!useReleaseFlag('chatbotAddressValidationEnabled')) {
    validationTypeOptions = validationTypeOptions.filter(
      (opt) => opt.value !== 'address',
    );
  }

  if (writebackDataType && writebackDataType !== 'string') {
    const allowedValidationTypes =
      writebackDataTypesToValidationTypes.get(writebackDataType);
    if (allowedValidationTypes) {
      hasFixedDataType = false;
      validationTypeOptions = dataTypeOptions.filter((dataTypeOption) =>
        allowedValidationTypes.includes(dataTypeOption.value),
      );
    } else {
      hasFixedDataType = true;
    }
  } else {
    hasFixedDataType = false;
  }

  const error = hasSubmitted ? validateNode(node, graph) : null;

  const nodeVariables = findAncestorNodeVariables(graph, defaultNode, edgeId);

  const handleAddressChange =
    (addressFieldName: string) => (attributeMetadata) => {
      const writebackFieldId = getWbFieldId(attributeMetadata);
      dispatch({
        type: 'set_address_writeback',
        payload: addressFieldName,
        metadata: {
          writebackFieldId,
        },
      });
    };

  return (
    <EditorContainer
      onUpdate={update}
      state={state}
      attributeWritebackInfo={attributeWritebackInfo}
      error={error}
      {...props}
    >
      <PadSection>
        <MessageDraftEditor
          label={t('Question', 'Question')}
          placeholder={`${t('Enter_a_question', 'Enter a question')}...`}
          defaultValue={node.text_draft_json}
          onChange={(text_draft_json) => update({text_draft_json})}
          entityType={entityType}
          isFirstSmsMessage={isFirstSmsMessage}
          extraCharacterCount={
            node.answers
              ? node.answers.reduce(
                  (length, answer) =>
                    length + 'A: \n'.length + answer.label.length,
                  0,
                )
              : 0
          }
          error={error?.text_draft_json}
          readOnly={readOnly}
          useCase={graph.use_case}
          nodeVariables={nodeVariables}
        />
        {error?.text_draft_json?.errors?.includes(
          'INVALID_VARIABLE_PREFIX',
        ) && (
          <div className={classify(css.tableError, css.noPadding)}>{`$ ${t(
            'cannot_be_placed_before_a_variable',
            'cannot be placed before a variable',
          )}`}</div>
        )}

        {node.response_type !== 'address' && (
          <FormPair label={t('Writeback_ATS_Field', 'Writeback ATS Field')}>
            <LoadedVariablePicker
              entityType={entityType}
              value={node.writeback_field_id}
              attributeMetadata={writebackAttributes}
              onChange={(attributeMetadata) => {
                const writebackFieldId = getWbFieldId(attributeMetadata);
                dispatch({
                  type: 'set_writeback',
                  payload: writebackFieldId,
                  metadata: {
                    previous: attributeWritebackInfo,
                    next: attributeMetadata,
                  },
                });
              }}
              readOnly={readOnly || node.response_type === 'address'}
            />
          </FormPair>
        )}

        {node.response_type !== 'boolean' &&
          (!attributeWritebackInfo ||
            !(
              attributeWritebackInfo.possible_values ||
              attributeWritebackInfo.possible_answers
            )) && (
            <FormPair label={t('Validation_Type', 'Validation Type')}>
              <Menu
                value={node.response_type}
                options={validationTypeOptions}
                onChange={(option) => update({response_type: option.value})}
                disabled={readOnly || hasFixedDataType}
              />
            </FormPair>
          )}

        <FormPair label={t('Response_Type', 'Response Type')}>
          <Menu
            value={responseType}
            options={
              attributeWritebackInfo?.possible_values ||
              attributeWritebackInfo?.possible_answers
                ? writebackResponseTypeOptions
                : responseTypeOptions
            }
            onChange={(option) =>
              dispatch({
                type: 'change_buttons_type',
                payload: option.value,
              })
            }
            disabled={
              readOnly ||
              writebackDataType === 'boolean' ||
              node.response_type === 'address'
            }
          />
        </FormPair>
      </PadSection>

      {node.response_type === 'address' && (
        <PadSection>
          <AddressWritebackTable
            entityType={entityType}
            node={node}
            readOnly={readOnly}
            handleAddressChange={handleAddressChange}
            writebackAttributes={writebackAttributes}
          />
        </PadSection>
      )}

      {attributeWritebackInfo?.possible_values ||
      attributeWritebackInfo?.possible_answers ? (
        <WritebackButtonsTable
          node={node}
          dispatch={dispatch}
          readOnly={readOnly}
          error={error}
        />
      ) : (
        node.answers && (
          <CustomButtonsTable
            node={node}
            dispatch={dispatch}
            readOnly={readOnly}
            error={error}
          />
        )
      )}

      <PadSection>
        <FormGroup
          label={`${t('Exception_Message', 'Exception Message')}*`}
          tooltip={<ExceptionToolTip />}
        >
          <MultiLineTextInput
            placeholder={
              node.answers ? multipleChoiceErrorMessage : stringErrorMessage
            }
            value={node.question_error_message || ''}
            onChange={(event) =>
              update({
                question_error_message: event.currentTarget.value,
              })
            }
            disabled={readOnly}
          />
          {isSms && (node.question_error_message?.length ?? 0) > 160 && (
            <SMSLengthWarning />
          )}
        </FormGroup>
      </PadSection>
    </EditorContainer>
  );
}

function ButtonColumn({value, isMulti, onChange, onDone, isEditing, error}) {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const hoverProps = useHoverTip({
    content: error?.errors?.includes('SMS_KEYWORD')
      ? `${t(
          'Choices_cannot_be_SMS_keywords',
          'Choices cannot be SMS keywords',
        )}.`
      : `${t('Choices_cannot_be_numbers', 'Choices cannot be numbers')}.`,
    fixedTo: 'left',
  });

  return (
    <ColumnGrow {...(error ? hoverProps : null)}>
      {isEditing ? (
        <OneLineTextInput
          size="small"
          autoFocus
          onChange={onChange}
          onBlur={onDone}
          onKeyDown={(event) => {
            if (['Escape', 'Enter'].includes(event.key)) {
              onDone();
            }
          }}
          value={value}
          error={error && 'Error'}
        />
      ) : (
        <Button
          size="medium"
          type={error ? 'destructive' : 'primary'}
          multi={isMulti}
          disabled
          styleDisabled={false}
        >
          {value}
        </Button>
      )}
    </ColumnGrow>
  );
}

function ExceptionToolTip() {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  return (
    <MouseTip
      fixedTo="right"
      content={
        <div>
          <div>
            {`${t(
              'If_the_candidate_responds_with_an_invalid_question_the_chatbot_will_say_the_exception_message_An_example_would_be',
              'If the candidate responds with an invalid question, the chatbot will say the exception message. An example would be',
            )}:`}{' '}
          </div>
          <img
            src="/images/chat-exception-example.png"
            className={css.exceptionImage}
          />
        </div>
      }
    >
      <div className={css.exceptionToolTip}>
        <InfoIcon />
      </div>
    </MouseTip>
  );
}

function CustomButtonsTable(props: {
  node: QuestionNode,
  dispatch: QuestionDispatch<QuestionNode>,
  readOnly: boolean,
  error: ?Validation,
}) {
  const isBoolean = props.node.response_type === 'boolean';
  return (
    <ButtonsTable
      {...props}
      header={<ButtonsHeader />}
      allowsAdding={!isBoolean}
      isWriteback={isBoolean}
    />
  );
}

function ButtonsTable({
  dispatch,
  node,
  header,
  Columns,
  allowsAdding,
  isWriteback,
  readOnly,
  error,
}: {
  dispatch: QuestionDispatch<QuestionNode>,
  node: QuestionNode,
  header: React.Node,
  Columns?: React.ComponentType<{
    dispatch: QuestionDispatch<QuestionNode>,
    answer: Answer,
    answers: Answer[],
    isEditing: boolean,
    readOnly: boolean,
  }>,
  allowsAdding?: boolean,
  isWriteback?: boolean,
  readOnly: boolean,
  error: ?Validation,
}) {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const isSms = useSmsContext();

  const [editingAnswer, setEditingAnswer] = React.useState<?(Id | string)>();
  const getNewAnswer = () => ({id: nanoid(), value: '', label: ''});
  const answers = node.answers || [];

  const updateAnswer = (answerId: AnswerId, shape: $Shape<Answer>) => {
    dispatch({
      type: 'update',
      payload: {
        answers: answers.map((answer) =>
          answer.id === answerId ? {...answer, ...shape} : answer,
        ),
      },
    });
  };

  const handleMoveAnswer = (index, direction) => {
    const newAnswers = [...(node.answers || [])];
    const currentAnswer = newAnswers[index];
    if (direction > 0 && index > 0) {
      newAnswers[index] = newAnswers[index - 1];
      newAnswers[index - 1] = currentAnswer;
    } else if (direction < 0 && index < newAnswers.length - 1) {
      newAnswers[index] = newAnswers[index + 1];
      newAnswers[index + 1] = currentAnswer;
    }
    dispatch({
      type: 'update',
      payload: {answers: newAnswers},
    });
  };

  return (
    <Table>
      {error?.answers?.errors?.includes('TOO_MANY') && (
        <div className={css.tableError}>{`${t(
          'Too_many_answers_Try_disabling_some',
          'Too many answers. Try disabling some',
        )}.`}</div>
      )}
      {error?.answers?.errors?.includes('DUPLICATE') && (
        <div className={css.tableError}>
          {t(
            'Buttons_must_have_unique_labels',
            'Buttons must have unique labels',
          )}
        </div>
      )}
      <TableHeader className={css.questionEditorTableHeader}>
        {header}
      </TableHeader>
      <TableBody className={css.scrollable}>
        {answers.map((answer, index) => {
          const isEditing = editingAnswer === answer.id;
          return (
            // $FlowIssue[incompatible-type-arg] - the return here is good
            <TableRow key={answer.id}>
              {isSms ? (
                <div className={css.columnFirst}>
                  {getMultipleChoiceLabel(index)}
                </div>
              ) : (
                <div className={css.columnEdit}>
                  {!isEditing && !readOnly && (
                    <HoverIconButton
                      onClick={() => setEditingAnswer(answer.id)}
                    >
                      <EditIcon />
                    </HoverIconButton>
                  )}
                </div>
              )}

              <ButtonColumn
                value={answer.label}
                onChange={({currentTarget: {value}}) => {
                  const shape = isWriteback
                    ? {label: value}
                    : {
                        label: value,
                        value,
                      };
                  updateAnswer(answer.id, shape);
                }}
                onDone={() => setEditingAnswer(null)}
                isMulti={node.question_is_multiselect}
                isEditing={!readOnly && (isSms || isEditing)}
                error={error?.answers?.[String(answer.id)]}
              />
              <ColumnValue>{String(answer.value)}</ColumnValue>
              {!readOnly && answers.length > 1 && (
                <>
                  <div className={classify(css.columnDelete, css.hoverButton)}>
                    <HoverIconButton
                      onClick={
                        index > 0 ? () => handleMoveAnswer(index, 1) : undefined
                      }
                    >
                      <ArrowUpIcon
                        className={classify({[css.arrowIcon]: index > 0})}
                      />
                    </HoverIconButton>
                  </div>
                  <div className={classify(css.columnDelete, css.hoverButton)}>
                    <HoverIconButton
                      onClick={
                        index < answers.length - 1
                          ? () => handleMoveAnswer(index, -1)
                          : undefined
                      }
                    >
                      <ArrowDownIcon
                        className={classify({
                          [css.arrowIcon]: index < answers.length - 1,
                        })}
                      />
                    </HoverIconButton>
                  </div>
                </>
              )}
              {Columns && (
                <Columns
                  dispatch={dispatch}
                  answers={answers}
                  answer={answer}
                  isEditing={isEditing}
                  readOnly={readOnly}
                />
              )}
              {!readOnly && allowsAdding && answers.length > 1 && (
                <div className={css.columnDelete}>
                  <HoverIconButton
                    onClick={() => {
                      dispatch({
                        type: 'update',
                        payload: {
                          answers: without(answers, answer),
                        },
                      });
                    }}
                  >
                    <CancelIcon />
                  </HoverIconButton>
                </div>
              )}
            </TableRow>
          );
        })}
      </TableBody>
      {!readOnly && allowsAdding && (
        <TableFooter>
          <Button
            type="text"
            size="medium"
            icon={<ComposeIcon />}
            onClick={() => {
              const newAnswer = getNewAnswer();
              dispatch({
                type: 'update',
                payload: {answers: [...(node.answers || []), newAnswer]},
              });
              setEditingAnswer(newAnswer.id);
            }}
          >
            {isSms
              ? t('Add_choice', 'Add choice')
              : t('Add_button', 'Add button')}
          </Button>
        </TableFooter>
      )}
    </Table>
  );
}

function WritebackButtonsTable(props: {
  dispatch: QuestionDispatch<QuestionNode>,
  node: QuestionNode,
  readOnly: boolean,
  error: ?Validation,
}) {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const checkToggledAnswers = (answers: Answer[]): CheckedValue => {
    let enabledValues: number = 0;
    let disabledValues: number = 0;

    answers.forEach((answer) => {
      if (answer.disabled) {
        disabledValues++;
      }
      if (!answer.disabled) {
        enabledValues++;
      }
    });
    const output: CheckedValue =
      disabledValues > 0 && enabledValues > 0
        ? 'mixed'
        : disabledValues === 0 && enabledValues > 0
        ? 'true'
        : 'false';

    return output;
  };

  const areAllToggled: CheckedValue = checkToggledAnswers(
    props.node.answers ?? [],
  );

  const handleToggle = () => {
    const shouldBeDisabled = areAllToggled === 'true';
    props.dispatch({
      type: 'update',
      payload: {
        answers: props.node.answers?.map((answer) => ({
          ...answer,
          disabled: shouldBeDisabled,
        })),
      },
    });
  };

  return (
    <ButtonsTable
      {...props}
      header={
        <>
          <ButtonsHeader className={classify(css.questionEditorButtonHeader)} />
          <ColumnGrow className={classify(css.questionEditorATSHeader)}>
            {t('ATS_Value', 'ATS Value')}
          </ColumnGrow>
          <ColumnGrow className={classify(css.questionEditorSwitchAllWrapper)}>
            <label onClick={handleToggle}>{`${t(
              'Enable_All',
              'Enable All',
            )}?`}</label>
            <Checkbox
              className={classify(css.questionEditorSwitchAll)}
              checked={areAllToggled}
              onChange={handleToggle}
            />
          </ColumnGrow>
        </>
      }
      Columns={WritebackColumns}
      isWriteback
    />
  );
}

function WritebackColumns({answer, answers, readOnly, dispatch}) {
  const editingAnswer = answer.id;

  const toggleEnabled = (isChecked) => {
    const isDisabled = isChecked === 'true' ? true : false;
    dispatch({
      type: 'update',
      payload: {
        answers: answers.map((answer) =>
          answer.id === editingAnswer
            ? {...answer, disabled: isDisabled}
            : answer,
        ),
      },
    });
  };

  // component knows checked means "enabled" but data is stored as "disabled"
  // swap and stringify
  const checked = answer.disabled === true ? 'false' : 'true';

  return (
    <>
      <ColumnEnabled>
        <Checkbox
          checked={checked}
          onChange={() => {
            toggleEnabled(checked);
          }}
          disabled={readOnly}
        />
      </ColumnEnabled>
    </>
  );
}

function ButtonsHeader() {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const isSms = useSmsContext();
  return (
    <>
      <ColumnGrow>
        {isSms
          ? t('Multiple_Choice', 'Multiple Choice')
          : t('Buttons', 'Buttons')}
      </ColumnGrow>
    </>
  );
}

function ColumnEdit({onClick, isHidden}) {
  return (
    <div className={css.columnEdit}>
      {!isHidden && (
        <HoverIconButton className={css.mcEditButton} onClick={onClick}>
          <EditIcon />
        </HoverIconButton>
      )}
    </div>
  );
}

function AddressWritebackTable({
  entityType,
  node,
  readOnly,
  handleAddressChange,
  writebackAttributes,
}) {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const derivedAddressFields = getDerivedAddressFields(t);
  const DerivedFieldPair = ({fieldName}: {fieldName: string}) => {
    if (node.derived_field_map == null) {
      throw new Error(
        `Cannot edit derived field (${fieldName}) without a derived field map.`,
      );
    }
    const fieldMap = node.derived_field_map[fieldName];

    return (
      <FormPair label={fieldMap.field_label}>
        <LoadedVariablePicker
          entityType={entityType}
          value={fieldMap.writeback_field_id}
          attributeMetadata={writebackAttributes}
          onChange={handleAddressChange(fieldName)}
          readOnly={readOnly}
        />
      </FormPair>
    );
  };
  return (
    <>
      {derivedAddressFields.map(([fieldName]) => (
        <DerivedFieldPair key={fieldName} fieldName={fieldName} />
      ))}
    </>
  );
}
